/**
 * PanelRenderer — upgraded panel rendering with:
 * - Lazy-loaded panel components (React.lazy + Suspense)
 * - Per-panel error boundaries (one panel failing doesn't crash the page)
 * - Consistent PanelCard wrapper (title bar, content area, optional actions)
 * - Panel loading skeleton while data loads
 * - Entrance animations (fade-in + slide-up)
 *
 * planTEK Wave 1 — External Views portal.
 */
import { lazy, Suspense } from 'react';
import { PanelCard, PanelSkeleton as LayoutPanelSkeleton } from '@/components/external/ExternalViewLayout';
import type { ExternalPanel } from '@/components/external/ExternalViewLayout';
import { PanelErrorBoundaryWithRetry, PanelSkeleton } from '@/components/external/common';

// ---------------------------------------------------------------------------
// Mobile priority order for panels (lower = higher priority on mobile).
// On md+ grid this has no effect — panels flow naturally into columns.
// ---------------------------------------------------------------------------
const MOBILE_ORDER: Record<string, number> = {
  project_summary:   1,  // Project status FIRST
  project_progress:  2,  // Progress / status indicators SECOND
  client_actions:    2,
  communication:     2,
  deliverable_list:  3,  // Deliverables THIRD
  document_list:     3,  // R4: Close-out docs / portal documents
  project_intake:    3,
  project_timeline:  3,
  clickup_task_list: 3,
  file_delivery:     3,
  payment_summary:   4,  // Payment / invoices LAST
  invoice_list:      4,
};

// ---------------------------------------------------------------------------
// Lazy-loaded panel registry
// ---------------------------------------------------------------------------
const PANEL_COMPONENTS: Record<string, React.LazyExoticComponent<any>> = {
  project_summary:   lazy(() => import('./panels/ProjectSummaryPanel')),
  deliverable_list:  lazy(() => import('./panels/DeliverableListPanel')),
  document_list:     lazy(() => import('./panels/DocumentsListPanel')),
  invoice_list:      lazy(() => import('./panels/InvoiceListPanel')),
  project_progress:  lazy(() => import('./panels/ProjectProgressPanel')),
  project_intake:    lazy(() => import('./panels/ProjectIntakePanel')),
  file_delivery:     lazy(() => import('./panels/FileDeliveryPanel')),
  client_actions:    lazy(() => import('./panels/ClientActionsPanel')),
  communication:     lazy(() => import('./panels/CommunicationPanel')),
  clickup_task_list: lazy(() => import('./panels/ClickupTaskListPanel')),
  project_timeline:  lazy(() => import('./panels/ProjectTimelinePanel')),
  payment_summary:   lazy(() => import('./panels/PaymentSummaryPanel')),
};

// ---------------------------------------------------------------------------
// Fallback for unknown panel types
// ---------------------------------------------------------------------------
function GenericPanelContent({ data }: { data: any }) {
  if (!data) {
    return (
      <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
        No data available.
      </p>
    );
  }

  if (Array.isArray(data)) {
    const rows = data;
    if (rows.length === 0) {
      return (
        <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
          No items.
        </p>
      );
    }
    const keys = Object.keys(rows[0] || {});
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: `1px solid var(--border, #e5e7eb)` }}>
              {keys.map((k) => (
                <th
                  key={k}
                  className="text-left py-2 pr-4 font-medium capitalize"
                  style={{ color: 'var(--muted-foreground, #6b7280)' }}
                >
                  {k.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row: any, i: number) => (
              <tr
                key={i}
                style={{ borderBottom: i < rows.length - 1 ? `1px solid var(--border, #e5e7eb)` : 'none' }}
              >
                {keys.map((k) => (
                  <td
                    key={k}
                    className="py-2 pr-4 text-sm"
                    style={{ color: 'var(--foreground)' }}
                  >
                    {String(row[k] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Object — render as key-value
  return (
    <div className="space-y-2">
      {Object.entries(data).map(([k, v]) => (
        <div key={k} className="flex gap-2 text-sm">
          <span
            className="capitalize shrink-0 min-w-32"
            style={{ color: 'var(--muted-foreground, #6b7280)' }}
          >
            {k.replace(/_/g, ' ')}
          </span>
          <span style={{ color: 'var(--foreground)' }}>{String(v ?? '')}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single panel renderer
// ---------------------------------------------------------------------------
interface PanelRendererItemProps {
  panel: ExternalPanel;
  index: number;
  /** Public view token — forwarded to interactive panels (e.g. project_intake) */
  token?: string;
  /** Email of the current portal user — forwarded to acceptance-capable panels */
  userEmail?: string;
  /** Auto-accept a client action ID on mount (from email link) */
  autoAcceptActionId?: string;
  /** Auto-accept a deliverable ID on mount (from email link) */
  autoAcceptDeliverableId?: string;
}

export function PanelRendererItem({ panel, index, token, userEmail, autoAcceptActionId, autoAcceptDeliverableId }: PanelRendererItemProps) {
  const LazyComponent = PANEL_COMPONENTS[panel.type];
  // Apply mobile priority order via inline style so panels stack correctly
  // on small screens. On md+ the grid layout takes over and order is ignored.
  const mobileOrder = MOBILE_ORDER[panel.type] ?? 5;

  return (
    <div
      data-panel-type={panel.type}
      style={{ order: mobileOrder }}
    >
      <PanelCard title={panel.title} index={index}>
        <PanelErrorBoundaryWithRetry panelTitle={panel.title}>
          {LazyComponent ? (
            <Suspense fallback={<PanelSkeleton aria-label={`Loading ${panel.title}`} />}>
              <LazyComponent
                data={panel.data}
                token={token}
                panelIndex={index}
                userEmail={userEmail}
                autoAcceptActionId={panel.type === 'client_actions' ? autoAcceptActionId : undefined}
                autoAcceptDeliverableId={panel.type === 'deliverable_list' ? autoAcceptDeliverableId : undefined}
              />
            </Suspense>
          ) : (
            <GenericPanelContent data={panel.data} />
          )}
        </PanelErrorBoundaryWithRetry>
      </PanelCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full panel list renderer (all panels for a view)
// ---------------------------------------------------------------------------
interface PanelRendererProps {
  panels: ExternalPanel[];
  isLoading?: boolean;
  /** Public view token — forwarded to interactive panels (e.g. project_intake) */
  token?: string;
  /** Email of the current portal user */
  userEmail?: string;
  /** Auto-accept a client action on mount (from email ?accept_action=<id>) */
  autoAcceptActionId?: string;
  /** Auto-accept a deliverable on mount (from email ?accept_deliverable=<id>) */
  autoAcceptDeliverableId?: string;
}

export function PanelRenderer({ panels, isLoading = false, token, userEmail, autoAcceptActionId, autoAcceptDeliverableId }: PanelRendererProps) {
  if (isLoading) {
    return (
      <>
        {[0, 1, 2].map((i) => (
          <LayoutPanelSkeleton key={i} index={i} />
        ))}
      </>
    );
  }

  if (!panels || panels.length === 0) {
    return (
      <div
        className="col-span-full rounded-xl border border-dashed py-14 text-center"
        style={{ borderColor: 'var(--border, #e5e7eb)' }}
      >
        <p
          className="text-sm"
          style={{ color: 'var(--muted-foreground, #6b7280)' }}
        >
          No content panels configured for this view.
        </p>
      </div>
    );
  }

  return (
    <>
      {panels.map((panel, idx) => (
        <PanelRendererItem
          key={`${panel.type}-${idx}`}
          panel={panel}
          index={idx}
          token={token}
          userEmail={userEmail}
          autoAcceptActionId={autoAcceptActionId}
          autoAcceptDeliverableId={autoAcceptDeliverableId}
        />
      ))}
    </>
  );
}
